import {
    Avatar,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'util';
import ICircuit from '../../../models/ICircuit';
import CircuitServiceInstance from '../../../services/CircuitService';
import OntimeTable from '../../common/OntimeTable';
import { v4 as uuidv4 } from 'uuid';
import DateHelperInstance from '../../../services/DateHelper';
import axios from 'axios';
import OntimeBreadcrumb from '../../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme) => ({
    table: {
        tableLayout: 'auto'
    },
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    appBarSpacer: theme.mixins.toolbar,
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    }
}));

interface ICircuitErrors {
    name?: string;
    length?: string;
    info?: string
    location?: string;
    lowerThreshold?: string;
    higherThreshold?: string;
    timeZone?: string;
}

interface IEditCircuitDialogProps {
    circuit: ICircuit;
    onCompleteEdit: (circuit: ICircuit) => void;
    onCancelEdit: () => void;
}

function EditCircuitDialog(props: IEditCircuitDialogProps) {
    const { t } = useTranslation("common");
    const [currentCircuit, setCurrentCircuit] = useState<ICircuit>(props.circuit);
    const [errors, setErrors] = React.useState<ICircuitErrors>();
    const [timeZones, setTimeZones] = React.useState<string[]>([]);
    const handleClose = () => {
        props.onCancelEdit();
    }

    useEffect(() => {
        const fetchData = async () => {
            const tzs = await CircuitServiceInstance.getTimeZones();
            setTimeZones(tzs);
        }
        fetchData().catch(reason => {
            console.error(reason);
        });
    }, []);

    const handleCompleteEdit = () => {
        props.onCompleteEdit(currentCircuit);
        handleClose();
    }

    const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const maxSizeBytes = 2 * 1024 * 1024; // 2 MB
            if (file.size > maxSizeBytes) {
                setErrors({ ...errors, name: t<string>("circuitSetup.pictureTooLarge") });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setCurrentCircuit({ ...currentCircuit, picture: reader.result as string });
            };
            reader.onerror = () => {
                console.error("Failed to read the selected file.");
                setErrors({ ...errors, name: t<string>("circuitSetup.pictureReadError") });
            };
            reader.readAsDataURL(file);
        }
    };

    const checkCircuit = (circuitToValidate: ICircuit) => {
        const error: ICircuitErrors = {};
        let anyError = false;
        if (circuitToValidate) {
            if (circuitToValidate.name.length === 0) {
                error.name = format(t("commonErrors.requiredField"), t("circuitSetup.name"));
                anyError = true;
            }
            if (circuitToValidate.length <= 0) {
                error.length = t<string>("circuitSetup.invalidLength");
                anyError = true;
            }
            if (circuitToValidate.lowerThreshold === undefined) {
                error.lowerThreshold = format(t("commonErrors.requiredField"), t("circuitSetup.lowerThreshold"));
                anyError = true;
            }
            if (circuitToValidate.higherThreshold === undefined) {
                error.lowerThreshold = format(t("commonErrors.requiredField"), t("circuitSetup.higherThreshold"));
                anyError = true
            }
            if (circuitToValidate.higherThreshold < circuitToValidate.lowerThreshold) {
                error.higherThreshold = t<string>("circuitSetup.lowerHigherError");
                anyError = true;
            }
            if (circuitToValidate.timeZone.length === 0) {
                error.timeZone = format(t("commonErrors.requiredField"), t("circuitSetup.timeZone"));
                anyError = true;
            }
            // if (timeZones.indexOf(circuitToValidate.timeZone)) {
            //     error.timeZone = t("circuitSetup.invalidTimezone");
            //     anyError = true;
            // }
        }
        if (anyError === true) {
            setErrors(error);
        } else {
            setErrors(undefined);
        }
    }


    return (<Dialog open={true} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t("circuitSetup.dialogTitle")}</DialogTitle>
        <DialogContent>

            <TextField
                autoFocus
                error={Boolean(errors?.name)}
                helperText={errors?.name}
                margin="dense"
                id="name"
                label={t("circuitSetup.name")}
                type="text"
                fullWidth
                value={currentCircuit.name}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, name: e.target.value };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <TextField
                error={Boolean(errors?.length)}
                helperText={errors?.length}
                margin="dense"
                id="length"
                label={t("circuitSetup.length")}
                type="number"
                value={currentCircuit.length}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, length: parseInt(e.target.value) };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <TextField
                error={Boolean(errors?.location)}
                helperText={errors?.location}
                margin="dense"
                id="location"
                label={t("circuitSetup.location")}
                type="text"
                fullWidth
                value={currentCircuit.location}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, location: e.target.value };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <TextField
                error={Boolean(errors?.info)}
                helperText={errors?.info}
                margin="dense"
                id="info"
                label={t("circuitSetup.info")}
                type="text"
                fullWidth
                value={currentCircuit.info}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, info: e.target.value };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <TextField
                error={Boolean(errors?.lowerThreshold)}
                helperText={errors?.lowerThreshold}
                margin="dense"
                id="lowerThreshold"
                label={t("circuitSetup.lowerThreshold")}
                type="number"
                defaultValue={90}
                fullWidth
                value={currentCircuit.lowerThreshold?.getSeconds() + currentCircuit.lowerThreshold?.getMinutes() * 60}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, lowerThreshold: new Date(parseInt(e.target.value) * 1000) };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <TextField
                error={Boolean(errors?.higherThreshold)}
                helperText={errors?.higherThreshold}
                margin="dense"
                id="higherThreshold"
                label={t("circuitSetup.higherThreshold")}
                type="number"
                defaultValue={300}
                fullWidth
                value={currentCircuit.higherThreshold.getSeconds() + currentCircuit.higherThreshold.getMinutes() * 60}
                onChange={e => {
                    const newCircuit: ICircuit = { ...currentCircuit, higherThreshold: new Date(parseInt(e.target.value) * 1000) };
                    setCurrentCircuit(newCircuit);
                    checkCircuit(newCircuit);
                }}
            />
            <Select
                label={t("circuitSetup.timeZone")}
                value={currentCircuit.timeZone}
                type='string'
                
                onChange={(e: SelectChangeEvent) => {

                    setCurrentCircuit({ ...currentCircuit, timeZone: typeof e.target.value === 'string' ? e.target.value : '' });
                    checkCircuit(currentCircuit);
                }}>
                {timeZones.map(tz => {
                    // return (<option value={tz}>tz</option>)
                    return (<MenuItem key={tz} value={tz}>{tz}</MenuItem>)

                })}

            </Select>
            <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
                <Button variant="contained" component="label">
                    {t("circuitSetup.uploadPicture")}
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handlePictureChange}
                    />
                </Button>
                {(currentCircuit.picture || currentCircuit.logoUrl) && (
                    <Avatar
                        src={currentCircuit.picture || currentCircuit.logoUrl}
                        alt="Circuit"
                        variant="rounded"
                        sx={{ width: 56, height: 56 }}
                    />
                )}
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCompleteEdit} color="primary" disabled={errors !== undefined}>
                {t("commonActions.ok")}
            </Button>
            <Button onClick={handleClose} color="primary">
                {t("commonActions.cancel")}
            </Button>
        </DialogActions>
    </Dialog >);
}

function CircuitsSetup() {
    const [circuits, setCircuits] = useState<ICircuit[]>([]);
    const { t } = useTranslation("common");
    const [editingCircuit, setEditingCircuit] = useState<ICircuit | undefined>(undefined);
    const classes = useStyles();
    const columns = [
        { id: 'circuitId', numeric: false, searchable: true, hidden: true, rowId: true },
        { id: 'name', numeric: false, disablePadding: true, label: t("circuitSetup.name"), searchable: true },
        { id: 'location', numeric: false, disablePadding: true, label: t("circuitSetup.location"), searchable: true, priority: 3 },
        { id: 'info', numeric: false, disablePadding: true, label: t("circuitSetup.info"), searchable: true, priority: 3 },
        { id: 'length', numeric: true, disablePadding: true, label: t("circuitSetup.length"), searchable: false, priority: 2 },
        { id: 'lowerThreshold', numeric: false, disablePadding: true, label: t("circuitSetup.lowerThreshold"), searchable: false, priority: 1 },
        { id: 'higherThreshold', numeric: false, disablePadding: true, label: t("circuitSetup.higherThreshold"), searchable: false, priority: 1 },
        { id: 'timeZone', numeric: false, disablePadding: true, label: t("circuitSetup.timeZone"), searchable: false, priority: 3 }
    ];

    useEffect(() => {
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            const circuits = await CircuitServiceInstance.getCircuits(source.token);
            setCircuits(circuits);
        }
        fetchData().catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const itemClick = (event: any, circuitId: string) => {
        alert(circuitId);
    }

    const handleAdd = () => {
        const newCircuit: ICircuit = {
            circuitId: uuidv4(),
            info: '',
            length: 1000,
            location: '',
            name: '',
            logoUrl: '',
            lowerThreshold: new Date(90000), // 1:30
            higherThreshold: new Date(300000), // 5:00
            timeZone: 'Europe/Madrid'
        }
        setEditingCircuit(newCircuit);
    }
    const handleDelete = async (circuitIds: string[]) => {
        circuitIds.forEach(async (circuitId) => {
            await CircuitServiceInstance.deleteCircuit(circuitId);
        });
        const updatedCircuits = [...circuits.filter(c => circuitIds.indexOf(c.circuitId) === -1)];      
        setCircuits(updatedCircuits);

    }
    const handleEdit = (circuitId: string) => {
        if (circuits) {
            const i = circuits.findIndex(c => c.circuitId === circuitId);
            if (i !== -1) {
                const circuit: ICircuit = {
                    ...circuits[i],
                    lowerThreshold: DateHelperInstance.parseTimeString(circuits[i].lowerThreshold.toString()),
                    higherThreshold: DateHelperInstance.parseTimeString(circuits[i].higherThreshold.toString())
                };
                setEditingCircuit(circuit);
            }
        }
    }

    const handleCompleteEdit = async (circuit: ICircuit) => {
        let savedCircuit = await CircuitServiceInstance.upsertCircuit(circuit);
        const updatedCircuits = [...circuits.filter(c => c.circuitId !== circuit.circuitId), savedCircuit];
        setCircuits(updatedCircuits);
    }

    return (
        <React.Fragment>
            {editingCircuit === undefined ? (<></>) : (<EditCircuitDialog
                circuit={editingCircuit}
                onCompleteEdit={handleCompleteEdit}
                onCancelEdit={() => setEditingCircuit(undefined)} />)}
            <main className={classes.content}>
                <div className={classes.appBarSpacer} />
                <Container maxWidth="lg" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <OntimeBreadcrumb nameMap={{}} current={t('circuitSetup.title')} />
                        </Grid>
                        <Grid item xs={12}>
                            <OntimeTable
                                tableItems={circuits}
                                columns={columns}
                                defaultOrderColumn={"name"}
                                dense={false}
                                title={t("circuitSetup.title")}
                                editable={true}
                                itemClick={itemClick}
                                onAdd={handleAdd}
                                onDelete={handleDelete}
                                onEdit={handleEdit}
                            >
                            </OntimeTable>
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </React.Fragment>
    );

}

export default CircuitsSetup;
